/**
 * @file - User verification component.
 */
import {
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    Renderer2,
} from '@angular/core';
import {
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OtpErrorMessages } from '../../../shared/constants/otp-errors.constant';
import { JoinService } from '../../../core/services/join/join.service';
import { AuthenticationService } from '../../../core/services/authentication/authentication.service';
import { OtpReason } from '../../../shared/constants/otp-reason.constant';
import { Subscription } from 'rxjs';
import { DialogHandlerService } from '../../../core/services/dialog-handler/dialog-handler.service';
import { ScriptService } from '../../../core/services/script/script.service';
import { scripts } from '../../../shared/constants/script.constant';
import { environment } from '../../../../environments/environment';
import {
    startUserSessionMonitor,
    resetUserToken,
} from '../../../shared/utitlity-functions/utility-functions';
import { UiMessageCodes } from '../../../shared/constants/info-messages.constant';
import { UserDataService } from '../../../core/services/user-data/user-data.service';
import { FunnelGuardSyncService } from '../../../core/services/driver/funnel-guard-sync.service';
import { RoutePaths } from '../../../shared/constants/route-paths.constant';
import { DriverStatusEnum } from '../../../shared/constants/driver-status.constant';

/**
 *
 */
@Component({
    standalone: false,
    selector: 'octo-user-verification',
    templateUrl: './user-verification.component.html',
    styleUrls: ['./user-verification.component.scss'],
})
export class UserVerificationComponent
    implements OnInit, AfterContentInit, OnDestroy
{
    isTimer: boolean = false;
    timeLeft: number = 30;
    interval: any;
    submitted: boolean = false;
    lastDigits: any = '';
    user: any = { isLogin: false, noAccess: false };
    errorText: any = '';
    error: boolean = false;
    status: any = '';
    submitting: boolean = false;
    isAccountLocked: boolean = false;
    userError$ = new Subscription();
    userErrors: any = {};
    msg: any = OtpErrorMessages.exhausted_attempts;
    isResendSuccess: boolean = false;
    verifyForm: any;
    adlibTag: any;
    fbPixelUs: any;
    fbPixelUsNoScript: any;
    fbPixelCanada: any;
    fbPixelCanadaNoScript: any;
    resetSubmitting: boolean = false;
    resetPhoneSubmitting: boolean = false;
    unlockTimeString: string = '';
    tiktokPixel: any;
    isNotUberConnected: boolean = false;
    isPhoneUpdate: boolean = false;
    isPhoneLocked: boolean = false;
    currentPhoneLastFour: string = '';
    uberConnectFlag = environment.UBER_CONNECT_SIGNUP_FLAG;
    emailDomain: string = '';

    /**
     * Constructor for user verification component.
     *
     * @param {joinService} joinService Join service.
     * @param {router} router Router.
     * @param {AuthenticationService} authService Authentication service.
     * @param {dialogService} dialogService Dialog service.
     * @param {renderer} renderer Renderer.
     * @param {scriptService} scriptService Script service.
     */
    constructor(
        private readonly joinService: JoinService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly authService: AuthenticationService,
        private readonly dialogService: DialogHandlerService,
        private readonly renderer: Renderer2,
        private readonly scriptService: ScriptService,
        private readonly userService: UserDataService,
        private readonly funnelGuardSyncService: FunnelGuardSyncService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    /**
     * Function called after content is initialized.
     */
    ngAfterContentInit(): void {
        startUserSessionMonitor();
    }

    /**
     * Function called on component initialization.
     */
    ngOnInit(): void {
        this.isPhoneUpdate = this.route.snapshot.queryParamMap.get('type') === 'phone_update';
        this.isPhoneLocked = this.route.snapshot.queryParamMap.get('locked') === 'true';
        if (this.isPhoneUpdate) {
            this.currentPhoneLastFour = sessionStorage.getItem('current_phone_last_four') || '';
        }
        resetUserToken();
        if (environment.production && environment.shouldAddPixel) {
            // pixel scripts
            this.adlibTag = this.scriptService.loadJsScript(
                this.renderer,
                scripts.adlib
            );
            this.fbPixelUs = this.scriptService.loadJs(
                this.renderer,
                scripts['fb-us']
            );
            this.fbPixelUsNoScript = this.scriptService.loadNoScriptImg(
                this.renderer,
                scripts['fb-image-us']
            );
            this.fbPixelCanada = this.scriptService.loadJs(
                this.renderer,
                scripts['fb-canada']
            );
            this.fbPixelCanadaNoScript = this.scriptService.loadNoScriptImg(
                this.renderer,
                scripts['fb-image-canada']
            );
            this.tiktokPixel = this.scriptService.loadJs(
                this.renderer,
                scripts['tiktok-base']
            );
        }

        let data = localStorage.getItem('user');
        if (data) {
            this.user = JSON.parse(data);
            this.joinService.setUserError({
                isLogin: false,
                noAccess: false,
                isLocked: this.isPhoneLocked,
                isReset: false,
                resetSuccess: false,
                isLogout: false,
                invalidLogin: false,
            });
        } else if (!this.isPhoneUpdate) {
            this.dialogService
                .confirmAction(
                    {
                        title: 'Error!',
                        message:
                            'We could not verify your session to send the security code. Please contact us for more information.',
                    },
                    false
                )
                .subscribe((data) =>
                    window.scroll({
                        top: 0,
                        left: 0,
                        behavior: 'smooth',
                    })
                );
        }
        this.verifyForm = new UntypedFormGroup({
            otp: new UntypedFormControl('', [
                Validators.required,
                Validators.pattern('\\d{6}'),
                Validators.minLength(6),
                Validators.maxLength(6),
            ]),
        });
        this.getInitialData();
    }

    /**
     * Function to get initial data.
     */
    getInitialData() {
        this.lastDigits = String(this.user.phone_number || 'xxxx').slice(-4);
        this.userError$ = this.joinService
            .getUserError()
            .subscribe((data: any) => {
                this.userErrors = data;
                if (this.userErrors?.isLocked) {
                    this.isAccountLocked = true;
                    this.cdr.detectChanges();
                }
                if (this.userErrors?.showTos) {
                    this.showTosModal('tosMessage');
                }
            });

        if (this.isPhoneLocked) {
            this.isAccountLocked = true;
            this.cdr.detectChanges();
            return;
        }

        // Get if user details from the local storage
        const isUserLocked = localStorage.getItem('is_locked');
        const driverStatus = localStorage.getItem('status');
        const driverAuthToken = sessionStorage.getItem('driverAuthToken') || '';

        if (!this.isPhoneUpdate && isUserLocked !== 'false') {
            this.joinService.setUserError({
                isLogin: false,
                noAccess: false,
                isLocked: true,
                isReset: false,
                resetSuccess: false,
                isLogout: false,
                invalidLogin: false,
            });
            this.authService.logOut();
            this.router.navigate([RoutePaths.LOGIN]);
        } else {
            this.status = driverStatus;
            this.user.token = driverAuthToken;
            if (!this.isPhoneUpdate) {
                this.status === 'approved' ||
                this.status === 'interested_dnq_other' ||
                this.status === 'interested_dnq_market' ||
                this.status === 'interested_dnq_rides'
                    ? this.showTosModal('tosForm')
                    : this.sentOtp('');
            }
        }
    }

    showTosModal(type: string): void {
        localStorage.setItem('isTosAccepted', 'false');
        this.dialogService.showTosModal(type).subscribe((data) => {
            if (data?.isTosAccepted) {
                this.sentOtp('statusChange');
                this.joinService.setUserError({
                    isLogin: false,
                    noAccess: false,
                    isLocked: false,
                    isReset: false,
                    resetSuccess: false,
                    isLogout: false,
                    invalidLogin: false,
                    showTos: false,
                });
            }
        });
    }

    /**
     * Function to get OTP.
     *
     * @param {string} sendType Reason for OTP.
     * @returns {void}
     */
    sentOtp(sendType: string): void {
        let isTosAccepted = localStorage.getItem('isTosAccepted');
        if (
            isTosAccepted === 'false' &&
            (this.status === 'approved' ||
                this.status === 'interested_dnq_other' ||
                this.status === 'interested_dnq_market' ||
                this.status === 'interested_dnq_rides')
        ) {
            this.showTosModal('tosMessage');
            return;
        }
        this.verifyForm.patchValue({ otp: '' });
        if (sendType === 'resend') this.resetSubmitting = true;
        this.joinService.getOTP(OtpReason.login).subscribe({
            /**
             * Function to handle success response.
             *
             * @param {any} data Response data.
             */
            next: (data: any) => {
                this.error = false;
                this.lastDigits = data.data.phone_last_four;
                if (sendType == 'resend') {
                    this.resetSubmitting = false;
                    this.startTimer();
                }
                if (sendType == 'statusChange') {
                    this.dialogService.alert({
                        title: 'Success',
                        message: 'The code has been sent to your number.',
                    });
                }
                this.funnelGuardSyncService.setFunnelGuardSync(true);
            },

            /**
             * Function to handle error response.
             *
             * @param {any} error Error response.
             */
            error: (error: any) => {
                this.resetSubmitting = false;
                if (error.status != 485) {
                    if (
                        error?.error?.error.otp_status == 'exhausted_attempts'
                    ) {
                        this.joinService.setUserError({
                            isLogin: false,
                            noAccess: false,
                            isLocked: true,
                            isReset: false,
                            resetSuccess: false,
                            isLogout: false,
                            invalidLogin: false,
                        });
                        localStorage.removeItem('user');
                        this.isAccountLocked = true;
                        this.isResendSuccess = false;
                        this.unlockTimeString =
                            error?.error?.error?.unlock_time_str;
                        this.cdr.detectChanges();
                    } else {
                        const err = error?.error?.error;
                        const otpStatus = err?.otp_status?.length
                            ? err.otp_status
                            : 'exception';
                        this.lastDigits = err?.last_four?.length
                            ? err?.last_four
                            : 'xxxx';
                        this.errorText = OtpErrorMessages[otpStatus];

                        this.dialogService.alert({
                            title: 'Oops!',
                            message: this.errorText,
                        });
                    }
                }
                this.funnelGuardSyncService.setFunnelGuardSync(true);
                this.cdr.detectChanges();
            },
        });
    }

    /**
     * Function to start timer.
     */
    startTimer(): void {
        this.isTimer = true;
        this.interval = setInterval(() => {
            if (this.timeLeft > 1) {
                this.timeLeft--;
                this.cdr.detectChanges();
            } else {
                this.isTimer = false;
                this.timeLeft = 30;
                this.cdr.detectChanges();
                clearInterval(this.interval);
            }
        }, 1000);
    }

    /**
     * Function to verify OTP.
     *
     * @returns {void}
     */
    onSubmit(): void {
        this.submitted = true;
        if (!this.verifyForm.valid) {
            return;
        }
        this.submitting = true;

        if (this.isPhoneUpdate) {
            this.joinService.verifyPhoneUpdateOtp({ otp: this.verifyForm.value.otp }).subscribe({
                next: (data: any) => {
                    window.location.href = data?.data?.stripe_verification_url;
                },
                error: (error: any) => {
                    this.verifyForm.patchValue({ otp: '' });
                    this.submitting = false;
                    setTimeout(() => this.cdr.detectChanges());
                    if (error.status !== 485) {
                        const code = error?.error?.ui_message_code;
                        if (code == 'otp_exhausted') {
                            this.joinService.setUserError({
                                isLogin: false,
                                noAccess: false,
                                isLocked: true,
                                isReset: false,
                                resetSuccess: false,
                                isLogout: false,
                                invalidLogin: false,
                            });
                            this.isAccountLocked = true;
                            localStorage.removeItem('user');
                            this.isResendSuccess = false;
                            this.isAccountLocked = true;
                            this.cdr.detectChanges();
                        } else if (
                            error?.error?.ui_message_code &&
                            error?.error?.error?.remaining_attempt
                        ) {
                            let msg = UiMessageCodes[
                                code
                            ].replace(
                                '{}',
                                error.error.error?.remaining_attempt
                            );
                            this.dialogService.alert({
                                title: 'Oops!',
                                message: msg,
                            });
                        } else {
                            this.dialogService.alert({
                                title: 'Oops!',
                                message: error?.error?.message || error?.message || 'Invalid verification code. Please try again.',
                            });
                        }
                    }
                },
            });
            return;
        }

        let temp = {
            ...this.verifyForm.value,
            otp_reason: OtpReason.login,
        };
        this.joinService.validateOTP(temp).subscribe({
            /**
             * Function to handle success response.
             *
             * @param {any} data Response data.
             */
            next: (data: any) => {
                this.error = false;
                this.status = localStorage.getItem('status');
                const sessionAuthToken =
                    sessionStorage.getItem('driverAuthToken');
                sessionAuthToken
                    ? localStorage.setItem('driverAuthToken', sessionAuthToken)
                    : localStorage.removeItem('driverAuthToken');
                sessionStorage.removeItem('driverAuthToken');
                localStorage.removeItem('user');
                this.joinService.getJoinDetails().subscribe((res: any) => {
                    this.joinService.setUserData(res.data);
                    this.status = res.data.status;
                    localStorage.setItem('status', res.data.status);
                    localStorage.setItem('texted_logs', res.data.texted_logs);
                    localStorage.setItem(
                        'user',
                        JSON.stringify({ email: res.data.email })
                    );
                    res?.data?.driver_info?.lifetime_rides
                        ? localStorage.setItem(
                              'lifetime_rides',
                              res?.data?.driver_info?.lifetime_rides
                          )
                        : localStorage.setItem('lifetime_rides', '0');
                    let funnelStatuses = [
                        'tos_complete',
                        'driver_info_complete',
                        'profile_complete',
                        'payment_complete',
                        'proof_of_rides',
                    ];
                    this.funnelGuardSyncService.setFunnelGuardSync(true);
                    // TODO: REMOVE THIS AFTER UBER DEPLOYMENT
                    try {
                        this.emailDomain = res.data.email.split('@')[1];
                    } catch (error) {
                        console.log('Error in getting email domain');
                        this.emailDomain = 'gmail.com';
                    }
                    if (
                        res?.data?.uber_connect?.status?.toUpperCase() !==
                            'CONNECTED' &&
                        funnelStatuses.includes(res.data?.status) &&
                        (this.uberConnectFlag ||
                            environment.ALLOWED_DOMAINS.includes(
                                this.emailDomain
                            ))
                    ) {
                        this.submitting = false;
                        setTimeout(() => this.cdr.detectChanges());
                        this.isNotUberConnected = true;
                        this.cdr.detectChanges();
                    } else if (
                        res.data.status == DriverStatusEnum.ACTIVE_DRIVER ||
                        res.data.status == DriverStatusEnum.FORMER_DRIVER ||
                        res.data.status == DriverStatusEnum.MAIL_DELIVERED ||
                        res.data.status == DriverStatusEnum.FORMER_DRIVER_MAIL_DELIVERED
                    ) {
                        this.userService.fetchUserSummary().subscribe();
                        this.router.navigate([RoutePaths.DASHBOARD_SUMMARY]);
                    } else {
                        this.router.navigate([RoutePaths.ROOT_PATH]);
                    }
                });
            },
            /**
             * Function to handle error response.
             *
             * @param {any} error Error response.
             */
            error: (error: any) => {
                this.verifyForm.patchValue({ otp: '' });
                this.submitting = false;
                setTimeout(() => this.cdr.detectChanges());
                if (error.status != 485) {
                    if (error?.error?.error?.otp_status == 'exhausted_attempts') {
                        this.joinService.setUserError({
                            isLogin: false,
                            noAccess: false,
                            isLocked: true,
                            isReset: false,
                            resetSuccess: false,
                            isLogout: false,
                            invalidLogin: false,
                        });
                        this.isAccountLocked = true;
                        localStorage.removeItem('user');
                        this.isResendSuccess = false;
                        this.isAccountLocked = true;
                        this.unlockTimeString =
                            error?.error?.error?.unlock_time_str;
                    } else {
                        if (
                            error?.error?.ui_message_code &&
                            error?.error?.error?.remaining_attempt
                        ) {
                            let msg = UiMessageCodes[
                                error.error.ui_message_code
                            ].replace(
                                '{}',
                                error.error.error.remaining_attempt
                            );
                            this.dialogService.alert({
                                title: 'Oops!',
                                message: msg,
                            });
                        } else {
                            this.dialogService.alert({
                                title: 'Oops!',
                                message:
                                    OtpErrorMessages[
                                        error.error.error.otp_status
                                    ],
                            });
                        }
                        this.error = true;
                        this.errorText =
                            OtpErrorMessages[error.error.error.otp_status];
                    }
                }
            },
        });
    }

    resendPhoneOtp(): void {
        this.resetSubmitting = true;
        this.joinService.resendPhoneUpdateOtp().subscribe({
            next: () => {
                this.resetSubmitting = false;
                this.startTimer();
                this.cdr.detectChanges();
            },
            error: (error: any) => {
                this.resetSubmitting = false;
                this.cdr.detectChanges();
                this.dialogService.alert({
                    title: 'Error',
                    message: error?.error?.message || 'Unable to resend verification code. Please try again.',
                });
            },
        });
    }

    resetPhoneNumber(): void {
        this.resetPhoneSubmitting = true;
        this.joinService.resetPhone().subscribe({
            next: (data: any) => {
                this.resetPhoneSubmitting = false;
                this.cdr.detectChanges();
                if (data?.data?.eligible) {
                    sessionStorage.setItem('current_phone_last_four', data?.data?.current_phone_last_four || '');
                    this.router.navigate(['/set_phone']);
                } else {
                    let message;
                    if(data?.ui_message_code === 'max_attempts_exceeded' || data?.ui_message_code === 'used_within_6_months') {
                        message = 'You have attempted a phone number reset within the last six months. Please contact support for assistance: <a href="https://playoctopus.com/help" target="_blank">playoctopus.com/help</a>';
                        this.dialogService.alertWithCallback({
                            title: 'Reset Limit Exceeded',
                            message: message,
                        }).subscribe(() => {
                            this.router.navigate([RoutePaths.LOGIN]);
                        });
                    } else {
                        message = 'Please contact support to reset your number: <a href="https://playoctopus.com/help" target="_blank">playoctopus.com/help</a><br><br>Error code: Tracker Status Failed';
                        this.dialogService.alertWithCallback({
                            title: 'Assistance Required!',
                            message,
                        }).subscribe(() => {
                            this.router.navigate([RoutePaths.LOGIN]);
                        });
                    }
                }
            },
            error: (error: any) => {
                this.resetPhoneSubmitting = false;
                this.cdr.detectChanges();
                this.dialogService.alert({
                    title: 'Error',
                    message: error?.error?.message || error?.message || 'Unable to reset phone number. Please try again.',
                });
            },
        });
    }

    /**
     * Function called on destroy.
     */
    ngOnDestroy(): void {
        this.userError$.unsubscribe();
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
        if (environment.production && environment.shouldAddPixel) {
            this.scriptService.removeScript(this.renderer, this.adlibTag);
            this.scriptService.removeScript(this.renderer, this.fbPixelUs);
            this.scriptService.removeScript(
                this.renderer,
                this.fbPixelUsNoScript
            );
            this.scriptService.removeScript(this.renderer, this.fbPixelCanada);
            this.scriptService.removeScript(
                this.renderer,
                this.fbPixelCanadaNoScript
            );
            this.scriptService.removeScript(this.renderer, this.tiktokPixel);
        }
    }
}
